import { CASE_LIST_PERSIST_STORAGE_KEY } from './persistKeys';

export const getItemFromLocalStorage = (itemKey) => {
  if (itemKey) {
    return window.localStorage.getItem(itemKey);
  }

  return null;
};

/**
 * Get the authentication strategy type from localStorage
 * @returns {string|null} 'keycloak' | 'hybrid' | null
 */
export const getStrategyType = () => {
  return getItemFromLocalStorage('strategyType');
};

/**
 * sessionStorage entries that are NOT authentication state and must outlive the auth cleanup.
 *
 * FIN-13485: the Case Listing filters are persisted here. A token refresh that fails after an
 * idle gap runs the cleanup below and then sends the investigator back through login; wiping
 * sessionStorage wholesale reset their filters every time. The entry is owned (the persist
 * layer stamps the username next to the filters and restores them only for that user), so
 * keeping it across the cleanup leaks nothing to the next person on the same tab.
 */
export const PRESERVED_SESSION_STORAGE_KEYS = [CASE_LIST_PERSIST_STORAGE_KEY];

/**
 * Clear sessionStorage except the preserved, non-auth entries.
 * Everything else — Keycloak callback state, notification triggers — goes, as before.
 */
export const clearSessionStorageExceptPreserved = (
  preservedKeys = PRESERVED_SESSION_STORAGE_KEYS
) => {
  const kept = preservedKeys
    .map((key) => [key, sessionStorage.getItem(key)])
    .filter(([, value]) => value !== null);
  sessionStorage.clear();
  kept.forEach(([key, value]) => {
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      // The old single clear() could not fail halfway; a write-back can (quota, disabled
      // storage). The cleanup itself must still succeed — losing a preserved entry is the
      // pre-FIN-13485 behaviour, not an error worth surfacing to the user.
      console.error(`Could not preserve sessionStorage entry ${key}`, error);
    }
  });
};

/**
 * Clear all authentication-related localStorage and sessionStorage items
 * This should be called when user authentication fails or during logout
 * @param {boolean} keepErrorMessage - Whether to preserve error messages for display
 */
export const clearAuthenticationStorage = (keepErrorMessage = false) => {
  // Store error message if it exists and we want to keep it
  const errorMessage = keepErrorMessage
    ? localStorage.getItem('auth_error_message')
    : null;

  // Remove all authentication-related localStorage items
  localStorage.removeItem('id_token');
  localStorage.removeItem('username');
  localStorage.removeItem('userName');
  localStorage.removeItem('notification-status');
  localStorage.removeItem('roleCode');
  localStorage.removeItem('productId');
  localStorage.removeItem('strategyType');

  // Clear sessionStorage (it may contain authentication-related data), keeping the
  // owned, non-auth entries listed above
  clearSessionStorageExceptPreserved();

  // Restore error message if we want to keep it
  if (keepErrorMessage && errorMessage) {
    localStorage.setItem('auth_error_message', errorMessage);
  }
};
